import _ from 'underscore';
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { BundleCouponClassCodes } from '@bingads-webui-campaign-react/promotion-utils';

import { getCouponOfferContentStyles } from '../styles/coupon-offer-content-styles';
import { DefaultCurrencyCode, transformDateFormat, couponTypes, formatCost } from '../helpers';
import { CouponDetailsProps } from '../coupon-props';

export const CouponOfferContent = (props) => {
  const {
    couponDetails,
    showCouponCode,
    couponCodeString,
    showCouponIcon,
    showExpiryDate,
    showCurrentOffer,
    channelOfferText: channelOfferTextProp,
    getCouponCurrencyCode,
  } = props;

  const classes = useDefaultStyles(getCouponOfferContentStyles);
  const i18n = useContext(I18nContext);

  const channelOfferText = channelOfferTextProp || i18n.getString(_TL_('This offer only applies to ads on the Microsoft Advertising Network.'));

  const percentOffer = useMemo(() => {
    if (!couponDetails) {
      return null;
    }
    const currency = getCouponCurrencyCode(couponDetails.CurrencyId) || DefaultCurrencyCode;
    const couponAmount = formatCost(couponDetails.CouponValue, i18n, currency);
    const offerText = i18n.getString(
      _TL_('{{ percentOff }}% off your search advertising spend (up to {{ couponAmount }} off) for {{ numberOfDays }} days. {{channelOfferText}}'),
      {
        percentOff: couponDetails.PercentOff,
        couponAmount,
        numberOfDays: couponDetails.LimitForDaysToUseFromCustomerCreation,
        channelOfferText,
      }
    );

    return offerText;
  }, [couponDetails, getCouponCurrencyCode, i18n, channelOfferText]);

  const valueOffer = useMemo(() => {
    if (!couponDetails) {
      return null;
    }
    let offerText = '';
    const currency = getCouponCurrencyCode(couponDetails.CurrencyId) || DefaultCurrencyCode;
    const couponAmount = formatCost(couponDetails.CouponValue, i18n, currency);
    if (couponDetails.CouponTypeId === couponTypes.Performance) {
      const upfrontSpendAmount =
        formatCost(couponDetails.UpfrontSpending, i18n, currency);
      offerText = i18n.getString(_TL_('Spend {{ upfrontSpendAmount }} get {{ couponAmount }} off. {{channelOfferText}}'), { upfrontSpendAmount, couponAmount, channelOfferText });
    } else {
      offerText = i18n.getString(_TL_('Sign up bonus {{ couponAmount }}. {{ channelOfferText }}'), { couponAmount, channelOfferText });
    }

    // overwrite if met the requirement
    if (_.include(BundleCouponClassCodes, couponDetails.ClassName)) {
      offerText = i18n.getString(_TL_('New customers can get {{ couponAmount }} for every {{ upfrontSpendAmount }} you spend, with a maximum discount of {{maxValueAmount}}. {{ channelOfferText }}'), {
        couponAmount,
        upfrontSpendAmount: formatCost(couponDetails.UpfrontSpending, i18n, currency),
        maxValueAmount: formatCost(500, i18n, currency),
        channelOfferText,
      });
    }

    return offerText;
  }, [couponDetails, getCouponCurrencyCode, i18n, channelOfferText]);

  if (!couponDetails) {
    return null;
  }
  return (
    <div className={classNames(classes.offerContainer, 'coupon-offer-content')}>
      <div className={classes.couponDetailsContainer}>
        {showCouponIcon && <Glyphicon
          className={classNames(classes.couponIcon, 'coupon-solid-icon')}
          glyph="ba-Coupon"
        />}
        <div className={classNames(classes.myCurrentOffer, 'current-offer-container')}>
          {showCurrentOffer && <b className="current-offer-label">{i18n.getString(_TL_('Current offer'))}</b>}
          {couponDetails.CouponTypeId === couponTypes.Percent ?
            <div className="percent-offer-text">
              {percentOffer}
            </div>
            :
            <div className="value-offer-text">
              {valueOffer}
            </div>
          }
          <div className={classNames(classes.OfferHelpLink, 'offer-learn-more-link')}>
            <LearnMoreLink
              topic="app54864"
              linkText={i18n.getString(_TL_('Promotional offer details'))}
            />
          </div>
          {showCouponCode &&
            <div className={classNames(classes.offerDetailsRow, 'coupon-code')}>
              <div className={classes.columnFont}>{i18n.getString(_TL_('Offer code'))}</div>
              <div className={classNames(classes.valueFont, 'coupon-code-value')}>
                {couponCodeString}
              </div>
            </div>
          }
          {showExpiryDate &&
            <div className={classNames(classes.offerDetailsRow, 'offer-expiry-date')}>
              <div className={classNames(classes.columnFont, 'expiry-date-label')}>{i18n.getString(_TL_('Expiration date'))}</div>
              <div className={classNames(classes.valueFont, 'expiry-date-value')}>
                {transformDateFormat(couponDetails.ExpiryDate)}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

CouponOfferContent.propTypes = {
  couponDetails: CouponDetailsProps.isRequired,
  showCouponCode: PropTypes.bool.isRequired,
  couponCodeString: PropTypes.string.isRequired,
  showCouponIcon: PropTypes.bool,
  showExpiryDate: PropTypes.bool,
  showCurrentOffer: PropTypes.bool,
  channelOfferText: PropTypes.string,
  getCouponCurrencyCode: PropTypes.func,
};

CouponOfferContent.defaultProps = {
  showCouponIcon: true,
  showExpiryDate: true,
  showCurrentOffer: true,
  channelOfferText: undefined,
  getCouponCurrencyCode: _.noop,
};
